import { ArrowRight } from '@phosphor-icons/react';
import React, { type JSX, type RefObject, useEffect, useState } from 'react';
import { DEFAULT_PLAYER_NAME } from '../../../constants/player';
import { DEFAULT_THEME_NAME } from '../../../constants/themes';
import translations from '../../../translations';
import ButtonDefault from '../../buttons/ButtonDefault';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import FormGroup from '../../generic/FormGroup';
import Glyphicon from '../../generic/Glyphicon';
import Modal from '../../generic/modal/GenericModal';
import Loader from '../../loader/Loader';
import { isNullOrEmpty } from '../../../utils/guard';
import { useEnvironments } from './EnvironmentsProvider';
import EnvironmentTile from './EnvironmentTile';

type Props = {
    modalContainerRef: RefObject<HTMLDivElement | null>;
    isActive: boolean;
};

const EnvironmentsList = ({ modalContainerRef, isActive }: Props) => {
    const {
        getAllEnvironments,
        environmentsLoading,
        environments,
        saveEnvironment,
        managingEnvironments,
        setIsManagingEnvironments,
    } = useEnvironments();
    const [showAddEnvironmentModal, setShowAddEnvironmentModal] = useState(false);
    const [environmentName, setEnvironmentName] = useState<string | null>(null);
    const [environmentDescription, setEnvironmentDescription] = useState<string | null>(null);
    const [nextEnvironmentId, setNextEnvironmentId] = useState<string | null>(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const isNameValid = !isNullOrEmpty(environmentName);
    const isDescriptionValid = !isNullOrEmpty(environmentDescription);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    useEffect(() => {
        if (isActive) {
            getAllEnvironments();
        }
    }, [isActive]);

    const save = async () => {
        setHasSubmitted(true);

        if (isNameValid && isDescriptionValid && nextEnvironmentId) {
            const environment = {
                name: environmentName,
                nextEnvironmentId: nextEnvironmentId,
                description: environmentDescription,
                defaultThemeName: DEFAULT_THEME_NAME,
                defaultPlayerName: DEFAULT_PLAYER_NAME,
            };
            await saveEnvironment(environment);
            await getAllEnvironments();
            setIsManagingEnvironments(false);
            setShowAddEnvironmentModal(false);
            setHasSubmitted(false);
        }
    };

    let content: JSX.Element | null = null;
    if (environmentsLoading) {
        content = <Loader message={translations.ENVIRONMENT_loading} />;
    } else {
        content = (
            <>
                <Modal
                    show={showAddEnvironmentModal}
                    container={modalContainerRef?.current}
                    onHide={() => {
                        setShowAddEnvironmentModal(false);
                        setHasSubmitted(false);
                    }}
                    title={translations.ENVIRONMENT_add_new}
                    renderBody={() => (
                        <>
                            <FormGroup
                                label="Name"
                                htmlFor="environment-name"
                                isRequired
                                validationMessage="Name field is required"
                                isValid={isNameValid}
                                showValidation={hasSubmitted}
                            >
                                <input
                                    id="environment-name"
                                    className="form-control form-control-dynamic"
                                    type="text"
                                    onChange={(e) => setEnvironmentName(e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup
                                label="Description"
                                htmlFor="environment-description"
                                isRequired
                                validationMessage="Description field is required"
                                isValid={isDescriptionValid}
                                showValidation={hasSubmitted}
                            >
                                <textarea
                                    id="environment-description"
                                    className="form-control form-control-textarea"
                                    onChange={(e) => setEnvironmentDescription(e.target.value)}
                                    rows={3}
                                />
                            </FormGroup>
                        </>
                    )}
                    className="config-modal"
                    renderFooter={() => (
                        <>
                            <ButtonDefault
                                onClick={() => {
                                    setShowAddEnvironmentModal(false);
                                    setHasSubmitted(false);
                                }}
                            >
                                {translations.ENVIRONMENT_cancel}
                            </ButtonDefault>

                            <ButtonPrimary
                                onClick={() => {
                                    save();
                                }}
                            >
                                {translations.ENVIRONMENT_save}
                            </ButtonPrimary>
                        </>
                    )}
                />

                <div className="margin-top wide-tiles-container">
                    {environments.map((environment) => (
                        <React.Fragment key={environment.id}>
                            <EnvironmentTile environment={environment} key={environment.id} />

                            {environment.nextEnvironmentId === null ? (
                                ''
                            ) : managingEnvironments ? (
                                <ButtonPrimary
                                    className="btn-sm wide-tile-add"
                                    onClick={() => {
                                        setNextEnvironmentId(environment.nextEnvironmentId || null);
                                        setEnvironmentName(null);
                                        setEnvironmentDescription(null);
                                        setHasSubmitted(false);
                                        setShowAddEnvironmentModal(true);
                                    }}
                                    testId={`btnAddEnvironment${environment.name}`}
                                >
                                    <Glyphicon glyph="plus" />
                                </ButtonPrimary>
                            ) : (
                                <div className="wide-tile-arrow">
                                    <ArrowRight weight="fill" size={25} />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </>
        );
    }

    return <div>{content}</div>;
};

export default EnvironmentsList;
